import { DatabricksDashboard } from '@databricks/aibi-client';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import Text, { TextVariant } from 'src/components/atoms/Text';
import { ShowState } from 'src/components/molecules/ShowState';

import { defaultInstance } from 'src/api/instance';
import { useApplicationTrackingAccess } from 'src/features/applicationTracking/hooks';
import { configVars } from 'src/helpers';

import { Container, DashboardContainer, ErrorContainer, LoadingContainer } from './styles';

interface DatabricksToken {
  access_token: string;
  expires_in: number;
  token_type: string;
}

const ApplicationTrackingPage: React.FC = () => {
  const {
    hasAccess,
    isLoading: isAccessLoading,
    isError: isAccessError
  } = useApplicationTrackingAccess();
  const dashboardClientRef = useRef<DatabricksDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchToken = useCallback(async (targetDashboardId: string) => {
    try {
      const tokenResponse: DatabricksToken = await defaultInstance.get(
        `${configVars.server_url}/v2/databricks/token?dashboard_id=${targetDashboardId}`
      );

      if (!tokenResponse.access_token) {
        throw new Error('Failed to obtain Databricks token');
      }

      return tokenResponse.access_token;
    } catch (err) {
      console.error('Failed to fetch Databricks token:', err);
      throw err;
    }
  }, []);

  const initializeDashboard = useCallback(async () => {
    if (dashboardClientRef.current) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const targetDashboardId =
        configVars.databricks.embedded_dashboards.application_tracking_dashboard_id;
      const { base_url: baseUrl, workspace_id: workspaceId } = configVars.databricks;

      if (!baseUrl || !targetDashboardId || !workspaceId) {
        throw new Error('Databricks configuration is missing');
      }

      const accessToken = await fetchToken(targetDashboardId);

      if (!containerRef.current) {
        throw new Error('Container element not found');
      }

      const client = new DatabricksDashboard({
        workspaceId,
        instanceUrl: baseUrl,
        container: containerRef.current,
        dashboardId: targetDashboardId,
        token: accessToken,
        colorScheme: 'light',
        getNewToken: () => fetchToken(targetDashboardId)
      });

      dashboardClientRef.current = client;

      client.initialize();
    } catch (err) {
      console.error('Failed to initialize Databricks dashboard:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
      dashboardClientRef.current = null;
    } finally {
      setIsLoading(false);
    }
  }, [fetchToken]);

  useEffect(() => {
    if (!hasAccess) {
      return undefined;
    }

    initializeDashboard();

    return () => {
      if (dashboardClientRef.current) {
        dashboardClientRef.current.destroy();
        dashboardClientRef.current = null;
      }
    };
  }, [hasAccess, initializeDashboard]);

  if (isAccessLoading) {
    return (
      <Container>
        <LoadingContainer>
          <Text variant={TextVariant.normal}>Loading Application Tracking...</Text>
        </LoadingContainer>
      </Container>
    );
  }

  if (isAccessError) {
    return (
      <Container>
        <ErrorContainer>
          <Text variant={TextVariant.normal} style={{ color: 'red' }}>
            Error loading Application Tracking. Refresh the page to try again.
          </Text>
        </ErrorContainer>
      </Container>
    );
  }

  if (!hasAccess) {
    return (
      <Container>
        <ShowState
          type="information"
          variant="error"
          message="You do not have permission to access Application Tracking."
        />
      </Container>
    );
  }

  return (
    <Container>
      {isLoading && (
        <LoadingContainer>
          <Text variant={TextVariant.normal}>Loading Application Tracking...</Text>
        </LoadingContainer>
      )}

      {error && (
        <ErrorContainer>
          <Text variant={TextVariant.normal} style={{ color: 'red' }}>
            Error loading Application Tracking: {error}
          </Text>
        </ErrorContainer>
      )}

      <DashboardContainer ref={containerRef} isVisible={!isLoading && !error} />
    </Container>
  );
};

export default ApplicationTrackingPage;
